import { conexao } from './conexao';
import { modoBuscaAtual } from './modoBusca';

// Liga um serviço online ao seu equivalente local. O modo manual (ver
// modoBusca.ts) tem prioridade sobre a detecção automática de conexão:
//  - 'offline': sempre local, nunca tenta o servidor (nem se estiver online).
//  - 'online': sempre tenta o servidor, e NÃO cai pro local se a conexão
//    cair no meio — o erro sobe do jeito que é (diferente do automático).
//  - 'automatico' (padrão): offline já conhecido
//    vai direto pro local (sem esperar timeout); online tenta o servidor e,
//    se a chamada falhar por falta de conexão (o ErrorInterceptor marca
//    offline nesse caso), cai no local na mesma hora.
// Com sucesso (em qualquer modo que tenha ido ao servidor), `aoSucesso`
// guarda a resposta no cache local pra quando a internet cair.
export const comOffline = async <T>(
  online: () => Promise<T | Error>,
  offline: () => Promise<T | Error>,
  aoSucesso?: (resultado: T) => unknown,
): Promise<T | Error> => {
  const modo = modoBuscaAtual();
  if (modo === 'offline') return offline();

  if (modo === 'online') {
    const resultado = await online();
    if (!(resultado instanceof Error) && aoSucesso) {
      try {
        await aoSucesso(resultado);
      } catch {
        // Cache é best-effort.
      }
    }
    return resultado;
  }

  if (conexao.estaOffline()) return offline();
  const resultado = await online();
  if (resultado instanceof Error) {
    return conexao.estaOffline() ? offline() : resultado;
  }
  if (aoSucesso) {
    try {
      await aoSucesso(resultado);
    } catch {
      // Cache é best-effort.
    }
  }
  return resultado;
};

// true quando o trabalho deve ficar só neste computador: sem conexão, OU o
// usuário escolheu o modo Offline no seletor (ver modoBusca.ts) — mesmo com
// internet disponível. Usado pelos salvamentos que têm versão offline (ex.:
// venda, ver VendasService.create/updateById), pra o modo valer também ao
// gravar, não só ao buscar.
export const trabalhandoOffline = (): boolean => modoBuscaAtual() === 'offline' || conexao.estaOffline();

export const MENSAGEM_MODO_OFFLINE = 'Modo de busca em Offline — esta função precisa do servidor. Troque para Automático ou Online.';

export const MENSAGEM_SO_ONLINE = 'Sem conexão com o servidor — esta função só funciona com internet.';

// Funções que não têm como funcionar offline (fechamento/sangria/relatórios
// do caixa, que dependem do saldo real no servidor) — responde na hora com
// uma mensagem clara em vez de esperar o timeout e mostrar "Nenhuma
// resposta do servidor".
export const somenteOnline = async <T>(online: () => Promise<T | Error>): Promise<T | Error> => {
  if (conexao.estaOffline()) return new Error(MENSAGEM_SO_ONLINE);
  const resultado = await online();
  if (resultado instanceof Error && conexao.estaOffline()) return new Error(MENSAGEM_SO_ONLINE);
  return resultado;
};
